import { useState, useRef, useEffect, useCallback } from "react";
import LandingPage from "./LandingPage";
import { Menu } from "lucide-react";
import { TOKENS } from "./tokens";
import "./App.css";
import Sidebar from "./components/Sidebar";
import ExtractionReviewModal from "./components/ExtractionReviewModal";
import { useWindowWidth } from "./hooks/useWindowWidth";
import { exportToExcel } from "./utils/exportExcel";
import { SubscriptionProvider, useSubscription } from "./context/SubscriptionContext";
import { PlanGate } from "./components/PlanGate";
import { DriveSettings } from "./components/DriveIntegration";
import DiscoverView from "./components/DiscoverView";
import { dequeueSync, getQueue, hasQueued, queueCount } from "./utils/offlineQueue";
import { SAMPLE_NOFO_DATA, SAMPLE_NOFO_META, SAMPLE_REPLAY_STAGES } from "./data/sampleNofo";

// ── fp-nofodesk-9 decomposition: extracted modules ──
import { FONT_UI, ThemeCtx, useT, THEME_MAP, DARK } from "./theme";
import { API, setToken, legacyToken, apiFetch, generateId } from "./utils/api";
import { loadOrgProfile, saveOrgProfile, apiTrackGrant, daysUntilDeadline, parsePDF, parseDocx, parseExcel } from "./utils/grants";
import DeadlineDashboard from "./pages/TrackerPage";
import CalendarView from "./pages/CalendarPage";
import BudgetPage from "./pages/BudgetPage";
import AwardSetupPage from "./pages/AwardSetupPage";
import FindingsPage from "./pages/FindingsPage";
import PropertyPage from "./pages/PropertyPage";
import EffortPage from "./pages/EffortPage";
import SubrecipientsPage from "./pages/SubrecipientsPage";
import DashboardView from "./pages/DashboardPage";
import PricingView from "./pages/PricingPage";
import { ChangePasswordSection, OrgProfileView, DataPrivacySection, DeleteAccountSection } from "./pages/SettingsPage";
import ChatPage from "./pages/ChatPage";

import NotificationBell from "./components/NotificationBell";
import WorkspaceSwitcher from "./components/WorkspaceSwitcher";
import TipBar from "./components/TipBar";
import { onbKey, loadOnboarding, logOnboardingEvent } from "./utils/onboarding";
import ProductTour, { TOUR_MODE, hasSeenTour } from "./components/ProductTour";

export { ThemeCtx, useT }; // backward compat for components importing from './App'


// ── MAIN APP ──────────────────────────────────────────────────
export default function GrantOS() {
  const [themeMode, setThemeMode] = useState(() => localStorage.getItem("nofodesk_theme") || "navy");
  const T = THEME_MAP[themeMode] || DARK;
  const appSub = useSubscription();

  useEffect(() => { localStorage.setItem("nofodesk_theme", themeMode); }, [themeMode]);

  // null = loading, false = not authed, object = authed user
  const [authUser, setAuthUser] = useState(null);

  const [activeView,     setActiveView]     = useState("dashboard");
  const [gosSubTab,      setGosSubTab]      = useState("active");
  const [gosPortFilter,  setGosPortFilter]  = useState("all");
  const [gosAppFilter,   setGosAppFilter]   = useState("all");
  const [messages,       setMessages]       = useState([]);
  const [input,          setInput]          = useState("");
  const [loading,        setLoading]        = useState(false);
  const [backendStatus,  setBackendStatus]  = useState("checking");
  const [sessionId]                         = useState(() => generateId());
  const [hasNOFO,        setHasNOFO]        = useState(false);
  const [chatKey,        setChatKey]        = useState(() => localStorage.getItem("nofodesk_chat_key") || null);
  const chatSaveTimer                       = useRef(null);
  const [orgProfile,     setOrgProfile]     = useState(() => loadOrgProfile());
  const _vw = useWindowWidth();
  const isDesktop = _vw >= 1024; // mobile layout now lives in ChatPage/useWindowWidth consumers
  const [sidebarOpen,    setSidebarOpen]    = useState(() => window.innerWidth >= 1024 && localStorage.getItem("sb_open") !== "false");
  const [sessions,       setSessions]       = useState(() => { try { return JSON.parse(localStorage.getItem("nofodesk_sessions_v1") || "[]"); } catch { return []; } });
  const [currentSessionId, setCurrentSessionId] = useState(() => generateId());

  const [alertDismissed,   setAlertDismissed]   = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showDiscovery,    setShowDiscovery]    = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [discoveryResults, setDiscoveryResults] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [discoveryQuery,   setDiscoveryQuery]   = useState('');
  // eslint-disable-next-line no-unused-vars
  const [discoveryError,   setDiscoveryError]   = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [discoverySources, setDiscoverySources] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [showSources,      setShowSources]      = useState(false);
  const [isOffline,        setIsOffline]        = useState(!navigator.onLine);
  const [offlineSyncStatus, setOfflineSyncStatus] = useState(null); // null | 'syncing' | 'done'

  const [pendingExtraction, setPendingExtraction] = useState(null);
  const [reviewSaving,      setReviewSaving]      = useState(false);
  // Legacy server-side checklist ids (extract/tracker/profile/…) — still
  // persisted via markChecklistItem for audit continuity; UI now reads the
  // 4-item onboarding state instead.
  // eslint-disable-next-line no-unused-vars
  const [checklistItems,    setChecklistItems]    = useState({});
  // Self-onboarding state (design/ONBOARDING_SPEC.md §5) — localStorage,
  // keyed per user; shape { sampleViewed, firstRealExtraction, profileDone,
  // deadlineSaved, dismissed }
  const [onboarding,        setOnboarding]        = useState(null);
  const [activeTip,         setActiveTip]         = useState(null); // { id, text, action? }
  const [showTour,          setShowTour]          = useState(false);
  const [tipsSeen,          setTipsSeen]          = useState([]);
  const [tipsMuted,         setTipsMuted]         = useState(() => localStorage.getItem("nofodesk_tips_muted") === "1");
  const [chatMode,          setChatMode]          = useState('chat'); // 'chat' | 'propose'
  const [writingStyle,      setWritingStyle]      = useState('professional');
  const [currentGrantData,  setCurrentGrantData]  = useState(null);

  const fileRef           = useRef(null);
  const bottomRef         = useRef(null);
  const inputRef          = useRef(null);
  const progressMsgIdRef  = useRef(null);
  const lastFileNameRef   = useRef(null);
  const lastContentRef    = useRef(null);
  const autoExtractRef    = useRef(null);
  const streamAbortRef    = useRef(null);   // AbortController for the active /chat-stream request
  const [isStreaming, setIsStreaming] = useState(false);

  // ── Self-onboarding: state helpers ─────────────────────────
  const onbUid = authUser ? (authUser.email || authUser.id || "anon") : null;

  // localStorage is the synchronous source of truth; React state mirrors it.
  // Guard + write + event all happen OUTSIDE the state updater — updaters
  // must stay pure (StrictMode invokes them twice).
  const markOnboarding = useCallback((flag, event) => {
    if (!onbUid) return;
    const base = loadOnboarding(onbUid) || {};
    if (base[flag]) return;
    const next = { ...base, [flag]: true };
    localStorage.setItem(onbKey(onbUid), JSON.stringify(next));
    setOnboarding(next);
    if (event) logOnboardingEvent(event);
  }, [onbUid]);

  // Initialize onboarding on login. Beat 1: pre-activation users land on
  // the primed Extract input; once onboarding is done (activated or
  // dismissed), the default landing view is Grant Discovery.
  useEffect(() => {
    if (!authUser) return;
    const uid = authUser.email || authUser.id || "anon";
    let onb = loadOnboarding(uid);
    if (!onb) {
      const legacy = localStorage.getItem(`nofodesk_onboarded_${uid}`);
      onb = {
        sampleViewed: false,
        firstRealExtraction: false,
        profileDone: !!loadOrgProfile(),
        deadlineSaved: false,
        dismissed: false,
      };
      localStorage.setItem(onbKey(uid), JSON.stringify(onb));
      if (!legacy) logOnboardingEvent("onboarding_signup");
    }
    setOnboarding(onb);
    setActiveView(onb.firstRealExtraction || onb.dismissed ? "discover" : "extract");

    // Product tour. TOUR_MODE === "full" auto-launches it for anyone who has
    // not seen it (GrantWizard-style); "hybrid" never auto-launches and waits
    // to be offered after the first extraction. Flipping that one constant in
    // ProductTour.jsx is the whole revert.
    if (TOUR_MODE === "full" && !hasSeenTour()) {
      // Let the extract view paint first, or the tour measures targets that
      // are not mounted yet and opens with everything centred.
      const t = setTimeout(() => {
        setShowTour(true);
        logOnboardingEvent("tour_started");
      }, 700);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  // Replay from anywhere: Help menu, checklist, etc.
  useEffect(() => {
    const replay = () => setShowTour(true);
    window.addEventListener("nofodesk:start-tour", replay);
    return () => window.removeEventListener("nofodesk:start-tour", replay);
  }, []);

  // ── Sample NOFO replay (Beat 2) — client-side only: zero Gemini call,
  // zero quota decrement, works with the backend cold or unreachable.
  const sampleTimersRef = useRef([]);
  const runSampleReplay = useCallback(() => {
    setActiveView("extract");
    sampleTimersRef.current.forEach(clearTimeout);
    sampleTimersRef.current = [];
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const progressId = generateId();
    const userMsg  = { role: "user", text: `${SAMPLE_NOFO_META.label} — ${SAMPLE_NOFO_META.title}` };
    const finalMsg = { id: progressId, role: "ai", type: "extraction", data: SAMPLE_NOFO_DATA, isSample: true };
    const appendProfilePrompt = (prev) =>
      (!loadOrgProfile() && !loadOnboarding(onbUid || "anon")?.profileDone)
        ? [...prev, { id: generateId(), role: "ai", type: "profile_prompt" }]
        : prev;
    if (reduced) {
      // Replay renders instantly under prefers-reduced-motion
      setMessages(prev => appendProfilePrompt([...prev, userMsg, finalMsg]));
    } else {
      const first = SAMPLE_REPLAY_STAGES[0];
      setMessages(prev => [...prev, userMsg, { id: progressId, role: "ai", type: "progress", message: first.message, percent: first.percent }]);
      SAMPLE_REPLAY_STAGES.slice(1).forEach(st => {
        sampleTimersRef.current.push(setTimeout(() => {
          setMessages(prev => prev.map(m => m.id === progressId ? { ...m, message: st.message, percent: st.percent } : m));
        }, st.delay));
      });
      sampleTimersRef.current.push(setTimeout(() => {
        setMessages(prev => appendProfilePrompt(prev.map(m => m.id === progressId ? finalMsg : m)));
      }, SAMPLE_REPLAY_STAGES[SAMPLE_REPLAY_STAGES.length - 1].delay + 700));
    }
    markOnboarding("sampleViewed", "onboarding_sample_viewed");
    markChecklistItem("extract");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onbUid, markOnboarding]);

  useEffect(() => () => { sampleTimersRef.current.forEach(clearTimeout); }, []);

  useEffect(() => {
    const onSample = () => runSampleReplay();
    const onFocusInput = () => {
      // The textarea only exists once the extract view has painted — retry
      // once so a view switch + focus in the same tick still lands
      if (inputRef.current) inputRef.current.focus();
      else setTimeout(() => inputRef.current?.focus(), 350);
    };
    window.addEventListener("nofodesk:run-sample", onSample);
    window.addEventListener("nofodesk:focus-input", onFocusInput);
    return () => {
      window.removeEventListener("nofodesk:run-sample", onSample);
      window.removeEventListener("nofodesk:focus-input", onFocusInput);
    };
  }, [runSampleReplay]);

  // ── Inline mini-profile save (Beat 3) + deadline save (Beat 4) ──
  const handleMiniProfileSave = useCallback((profile) => {
    const merged = { ...(loadOrgProfile() || {}), ...profile };
    saveOrgProfile(merged);
    setOrgProfile(merged);
    apiFetch(`${API}/db/org-profile`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ profile: merged }) }).catch(() => {});
    markOnboarding("profileDone", "onboarding_profile_completed");
    markChecklistItem("profile");
  }, [markOnboarding]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeadlineSave = useCallback(async (msgId, grantData) => {
    await apiTrackGrant(grantData);
    const daysLeft = daysUntilDeadline(grantData.timeline?.applicationDeadline);
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, saved: true, daysLeft } : m));
    markOnboarding("deadlineSaved", "onboarding_deadline_saved");
    markChecklistItem("tracker");
  }, [markOnboarding]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { localStorage.setItem("sb_open", sidebarOpen ? "true" : "false"); }, [sidebarOpen]);
  useEffect(() => { localStorage.setItem("nofodesk_sessions_v1", JSON.stringify(sessions.slice(0, 50))); }, [sessions]);

  // Handle return from Flutterwave checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("payment_status");
    if (status === "success") {
      setActiveView("pricing");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Open pricing when any gated feature fires the event
  useEffect(() => {
    const handler = () => setActiveView("pricing");
    window.addEventListener("nofodesk:open-pricing", handler);
    return () => window.removeEventListener("nofodesk:open-pricing", handler);
  }, []);

  // Auto-trigger extraction when navigating to extract view from Discovery
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeView === "extract" && autoExtractRef.current && !loading) {
      const url = autoExtractRef.current;
      autoExtractRef.current = null;
      setMessages(prev => [...prev, { role: "user", text: url }]);
      setInput("");
      setLoading(true);
      extractWithStream(url).finally(() => setLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]); // intentionally only watches activeView — fires once on view change, not on every loading state update

  const handleExtractGrant = useCallback((grant) => {
    autoExtractRef.current = grant.url;
    setInput(grant.url);
    setActiveView("extract");
  }, []);

  const flushOfflineQueue = useCallback(async () => {
    const q = getQueue();
    const entries = Object.entries(q);
    if (!entries.length) return;
    setOfflineSyncStatus('syncing');
    for (const [endpoint, { records }] of entries) {
      try {
        const res = await apiFetch(`${API}/db/${endpoint}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ records }),
        });
        if (res.ok || res.status === 403) dequeueSync(endpoint);
      } catch {}
    }
    setOfflineSyncStatus('done');
    setTimeout(() => setOfflineSyncStatus(null), 3000);
  }, []);

  useEffect(() => {
    const onOnline  = () => { setIsOffline(false); flushOfflineQueue(); };
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    // Flush any queued changes from a previous offline session on mount
    if (navigator.onLine && hasQueued()) flushOfflineQueue();
    return () => {
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [flushOfflineQueue]);

  // Auth check on mount + handle OAuth redirect params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Exchange one-time code for token (never store the raw token from URL)
    const oauthCode = params.get("code");
    if (params.get("login") === "success" && oauthCode) {
      window.history.replaceState({}, "", window.location.pathname);
      fetch(`${API}/auth/exchange-code`, {
        method: "POST",
        credentials: "include",   // the session arrives as an httpOnly cookie
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ code: oauthCode }),
      })
        .then(r => r.json())
        .then(d => {
          if (d.success) {
            setToken(true);
            apiFetch(`${API}/auth/me`).then(r => r.json()).then(d2 => setAuthUser(d2.user || false)).catch(() => setAuthUser(false));
          }
        })
        .catch(() => {});
      return;
    }
    // A session saved by the pre-cookie app is swapped for the cookie once,
    // so nobody is signed out by the change; then the stored copy is deleted.
    const migrate = legacyToken()
      ? apiFetch(`${API}/auth/session`, { method: "POST" }).then(r => { if (r.ok) setToken(true); }).catch(() => {})
      : Promise.resolve();
    migrate
      .then(() => apiFetch(`${API}/auth/me`))
      .then((r) => r.json())
      .then((d) => { setAuthUser(d.user || false); setToken(!!d.user); })
      .catch(() => setAuthUser(false));
  }, []);

  // Load org profile from Supabase on mount and after login
  useEffect(() => {
    if (!authUser) return;
    (async () => {
      try {
        const profileRes = await apiFetch(`${API}/db/org-profile`).then(r => r.json());
        if (profileRes.profile) {
          saveOrgProfile(profileRes.profile);
          setOrgProfile(profileRes.profile);
        }
      } catch { /* silent */ }
    })();
  }, [authUser?.id ?? authUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    let attempts  = 0;
    let timerId;
    const check = () => {
      apiFetch(`${API}/health`)
        .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
        .then(() => { if (!cancelled) setBackendStatus("ok"); })
        .catch(() => {
          if (cancelled) return;
          attempts++;
          if (attempts < 6) timerId = setTimeout(check, 2000);
          else setBackendStatus("down");
        });
    };
    check();
    return () => { cancelled = true; clearTimeout(timerId); };
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Auto-save extraction session whenever messages change
  useEffect(() => {
    if (messages.length === 0) return;
    const exMsg = messages.find(m => m.type === "extraction");
    const uMsg  = messages.find(m => m.role === "user");
    const title = exMsg?.data?.opportunityIdentification?.callTitle?.slice(0, 60)
      || uMsg?.text?.slice(0, 60)
      || "Untitled Extraction";
    setSessions(prev => {
      const exists = prev.find(s => s.id === currentSessionId);
      // key = server-side chat_key, used to dedupe against the cross-device
      // session index and to lazy-load/delete the session on other devices
      const updated = exists
        ? prev.map(s => s.id === currentSessionId ? { ...s, messages, title, key: chatKey || s.key || null, updatedAt: new Date().toISOString() } : s)
        : [{ id: currentSessionId, title, messages, key: chatKey || null, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), starred: false }, ...prev].slice(0, 200);
      return updated;
    });
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cross-device history: merge the server-side session index into the
  // local sidebar list. Messages are fetched on open, not up front.
  useEffect(() => {
    if (!authUser) return;
    apiFetch(`${API}/chat-sessions`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d?.sessions?.length) return;
        setSessions(prev => {
          const knownKeys = new Set(prev.map(s => s.key).filter(Boolean));
          const merged = [...prev];
          d.sessions.forEach(rs => {
            if (!rs.chat_key || knownKeys.has(rs.chat_key)) return;
            const src = rs.grant_source || rs.chat_key;
            merged.push({
              id: `remote_${rs.chat_key}`,
              key: rs.chat_key,
              title: src.startsWith("http") ? src.slice(0, 60) : src.slice(0, 60),
              messages: null, // lazy-loaded on open
              createdAt: rs.updated_at,
              updatedAt: rs.updated_at,
              starred: false,
            });
          });
          return merged
            .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
            .slice(0, 200);
        });
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  // eslint-disable-next-line no-unused-vars
  const startNewSession = () => {
    setMessages([]);
    setHasNOFO(false);
    setCurrentGrantData(null);
    setChatKey(null);
    localStorage.removeItem("nofodesk_chat_key");
    setInput("");
    setCurrentSessionId(generateId());
    setActiveView("extract");
  };

  // eslint-disable-next-line no-unused-vars
  const handleDiscoverGrants = async () => {
    setDiscoveryLoading(true); setDiscoveryResults(null); setDiscoveryError(null); setDiscoverySources([]); setShowDiscovery(true); setShowSources(false);
    try {
      const res  = await apiFetch(`${API}/discover-grants`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ orgProfile, query: discoveryQuery || undefined }) });
      const json = await res.json();
      if (!res.ok) { setDiscoveryError(json.message || json.error || 'Search failed'); setDiscoveryResults([]); }
      else { setDiscoveryResults(json.grants || []); setDiscoverySources(json.sources || []); }
    } catch { setDiscoveryError('Unable to connect to search service.'); setDiscoveryResults([]); }
    setDiscoveryLoading(false);
  };

  const handleLoadSession = (session) => {
    setCurrentSessionId(session.id);
    setActiveView("extract");
    if (session.messages) {
      setMessages(session.messages);
      const ex = session.messages.find(m => m.type === "extraction");
      setHasNOFO(!!ex);
      // Restore the grant context too. Without this, switching to a tab left
      // currentGrantData pointing at the PREVIOUS grant — so chat answered
      // about the wrong document and the budget builder loaded the wrong
      // funder's rules. Only became reachable once tabs made switching easy.
      setCurrentGrantData(ex?.data || null);
      setChatKey(session.key || null);
      if (session.key) localStorage.setItem("nofodesk_chat_key", session.key);
      return;
    }
    // Remote session from another device — fetch messages + grant context
    if (!session.key) { setMessages([]); setHasNOFO(false); return; }
    setMessages([{ id: generateId(), role: "ai", type: "progress", message: "Loading session…", percent: 50 }]);
    apiFetch(`${API}/chat-session?key=${encodeURIComponent(session.key)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const msgs = data?.messages || [];
        setMessages(msgs);
        setHasNOFO(msgs.some(m => m.type === "extraction") || !!data?.grantContext);
        if (data?.grantContext) setCurrentGrantData(data.grantContext);
        setChatKey(session.key);
        localStorage.setItem("nofodesk_chat_key", session.key);
        setSessions(prev => prev.map(s => s.id === session.id ? { ...s, messages: msgs } : s));
      })
      .catch(() => { setMessages([]); setHasNOFO(false); });
  };

  const handleDeleteSession = (id) => {
    const target = sessions.find(s => s.id === id);
    if (target?.key) {
      apiFetch(`${API}/chat-session?key=${encodeURIComponent(target.key)}`, { method: "DELETE" }).catch(() => {});
    }
    setSessions(prev => prev.filter(s => s.id !== id));
    if (id === currentSessionId) startNewSession();
  };

  // Shared post-real-extraction onboarding beats (fp-11): one contextual
  // prompt (deadline first, else mini-profile — never stacked) plus the
  // activation event. Both review outcomes are real extractions, so both
  // Confirm (keep in chat) and Save to Apps fire these.
  const fireRealExtractionBeats = (confirmedData) => {
    const onb = onboarding || loadOnboarding(onbUid || "anon") || {};
    const isFirstReal = !onb.firstRealExtraction;
    setMessages(prev => {
      if (!onb.deadlineSaved && !prev.some(m => m.type === "deadline_prompt" && !m.saved)) {
        return [...prev, { id: generateId(), role: "ai", type: "deadline_prompt", data: confirmedData, saved: false }];
      }
      if (!loadOrgProfile() && !onb.profileDone && !prev.some(m => m.type === "profile_prompt")) {
        return [...prev, { id: generateId(), role: "ai", type: "profile_prompt" }];
      }
      return prev;
    });
    markChecklistItem("extract");
    // ACTIVATION: first real extraction — audit event + activation ping
    // (server emails kelvin@nofodesk.com and dedupes repeats; never blocks)
    if (isFirstReal) markOnboarding("firstRealExtraction", "onboarding_first_real_extraction");
  };

  const handleConfirmExtraction = (fields, correctionsMade) => {
    if (!pendingExtraction) return;
    const confirmedData = { ...pendingExtraction.data };
    setMessages((prev) => prev.map((m) =>
      m.id === pendingExtraction.progressId
        ? { id: pendingExtraction.progressId, role: "ai", type: "extraction", data: confirmedData }
        : m
    ));
    lastFileNameRef.current = null;
    setPendingExtraction(null);
    fireRealExtractionBeats(confirmedData);
  };

  const handleSaveToApps = async (fields, correctionsMade) => {
    if (!pendingExtraction) return;
    setReviewSaving(true);
    try {
      const res = await apiFetch(`${API}/extraction/confirm`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ extractionId: pendingExtraction.progressId, correctedData: fields, verified: true, correctionsMade }),
      });
      if (!res.ok) { const j = await res.json(); alert(j.error || "Save failed"); setReviewSaving(false); return; }
    } catch { /* non-blocking */ }
    const confirmedData = { ...pendingExtraction.data, _verified: true };
    setMessages((prev) => prev.map((m) =>
      m.id === pendingExtraction.progressId
        ? { id: pendingExtraction.progressId, role: "ai", type: "extraction", data: confirmedData }
        : m
    ));
    lastFileNameRef.current = null;
    setPendingExtraction(null);
    setReviewSaving(false);
    markChecklistItem("tracker");
    fireRealExtractionBeats(confirmedData);
  };

  const handleDiscardExtraction = () => {
    if (!pendingExtraction) return;
    setMessages((prev) => prev.filter(m => m.id !== pendingExtraction.progressId));
    lastFileNameRef.current = null;
    setPendingExtraction(null);
  };

  const handleReExtract = (focus) => {
    if (!pendingExtraction || !lastContentRef.current) return;
    setMessages((prev) => prev.filter(m => m.id !== pendingExtraction.progressId));
    setPendingExtraction(null);
    extractWithStream(lastContentRef.current, focus);
  };

  const attachFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    try {
      let text = "";
      if (name.endsWith(".pdf")) text = await parsePDF(file);
      else if (name.endsWith(".docx")) text = await parseDocx(file);
      else if (name.endsWith(".xlsx") || name.endsWith(".xls")) text = await parseExcel(file);
      else { alert("Unsupported file type. Use PDF, DOCX, or Excel."); return; }
      if (!text.trim()) { alert("Could not extract text from this file. Try a different file."); return; }
      // Same rule as a pasted grant: a new document opens its own tab rather
      // than replacing the extraction currently on screen.
      if (hasNOFO) startNewSession();
      lastFileNameRef.current = file.name;
      const fileSizeLabel = file.size > 1048576 ? `${(file.size/1048576).toFixed(1)} MB` : `${Math.round(file.size/1024)} KB`;
      setMessages(prev => [...prev, { role: "user", type: "file", fileName: file.name, fileSize: fileSizeLabel }]);
      setLoading(true);
      await extractWithStream(text);
      setLoading(false);
    } catch (err) { alert("File parse failed: " + err.message); }
    e.target.value = "";
  };

  const extractWithStream = async (content, focus) => {
    lastContentRef.current = content;
    const progressId = generateId();
    progressMsgIdRef.current = progressId;
    setMessages((prev) => [...prev, { id: progressId, role: "ai", type: "progress", message: "🚀 Starting extraction...", percent: 5 }]);

    try {
      const response = await apiFetch(`${API}/extract-stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, sessionId, focus: focus || undefined }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (errData.code === 'EXTRACTION_LIMIT') {
          setMessages((prev) => prev.map((m) => m.id === progressMsgIdRef.current
            ? { id: progressMsgIdRef.current, role: "ai", type: "extraction_limit", text: errData.error }
            : m));
          return;
        }
        throw new Error(errData.error || `Server error ${response.status}`);
      }

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // currentEvent MUST live outside the while loop — if event and data
      // lines arrive in different read chunks (common for large JSON), a
      // loop-scoped variable resets to "" before the data line is processed.
      let currentEvent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            try {
              const parsed = JSON.parse(line.slice(6).trim());
              if (currentEvent === "progress") {
                setMessages((prev) => prev.map((m) => m.id === progressId ? { ...m, message: parsed.message, percent: parsed.percent } : m));
              } else if (currentEvent === "result") {
                const sourceName = lastFileNameRef.current || (content.startsWith("http") ? content.slice(0, 80) : "Pasted text");
                const newKey = content.startsWith("http") ? content.slice(0, 500) : `paste_${Date.now()}`;
                setChatKey(newKey);
                localStorage.setItem("nofodesk_chat_key", newKey);
                setPendingExtraction({ progressId, data: parsed.data, sourceName });
                setCurrentGrantData(parsed.data);
                setHasNOFO(true);
              } else if (currentEvent === "hint") {
                setMessages((prev) => prev.map((m) => m.id === progressId ? { id: progressId, role: "ai", type: "hint", text: parsed.message, reason: parsed.reason, steps: parsed.steps } : m));
              } else if (currentEvent === "error") {
                setMessages((prev) => prev.map((m) => m.id === progressId ? { id: progressId, role: "ai", type: "error", text: parsed.message } : m));
              }
            } catch { /* malformed SSE line — skip */ }
          }
        }
      }
    } catch (err) {
      setMessages((prev) => prev.map((m) => m.id === progressMsgIdRef.current ? { id: progressMsgIdRef.current, role: "ai", type: "error", text: err.message } : m));
    }
  };

  // 12c: run the real Excel export client-side when the user explicitly asks
  // for one — the AI cannot attach files. Deliberately narrow: a spreadsheet
  // noun must sit next to a give-me verb. Bare "download" / "export" /
  // "spreadsheet" are everyday grant vocabulary ("where do I download the
  // forms?", "does this fund agricultural exports?") and must reach the AI.
  // Bias to false negatives: a miss costs one reply pointing at the Export
  // Excel button, a false hit silently swallows the user's question.
  const EXCEL_INTENT = /\b(export|download|generate|create|send|give|make|produce)\b[^.?!\n]{0,20}\b(excel|xlsx?|spreadsheet|csv|workbook)\b|\b(excel|xlsx?|spreadsheet|csv|workbook)\b[^.?!\n]{0,12}\b(export|download|file|version|copy)\b/i;
  // Asking about the funder's own template/form is a question, not an export.
  const FUNDER_DOC   = /\b(template|forms?|attachment|annex)\b/i;

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userText    = input.trim();
    const isURL       = userText.startsWith("http");
    // A pasted NOFO is document-shaped: thousands of characters, or long and
    // multi-line. Length alone is not enough — a detailed question is still a
    // question, and routing it to the extractor means it never gets answered.
    const isPastedDoc = !/\?\s*$/.test(userText) && (
      userText.length > 1500 ||
      (userText.length > 400 && userText.split("\n").filter(l => l.trim()).length >= 5)
    );
    const looksLikeGrant = isURL || isPastedDoc;
    const isFirstNOFO    = looksLikeGrant && !hasNOFO;
    // A second grant belongs in its OWN tab. Pasting one into a session that
    // already holds an extraction used to overwrite it — losing the grant you
    // were reading, and any comparison you were making between the two.
    const openInNewTab   = looksLikeGrant && hasNOFO && chatMode !== 'propose';

    // 12c: intercept Excel export intent — the AI can't attach files, so
    // handle it client-side with the real export and skip the AI call.
    // Only when an extraction is loaded; otherwise the message passes through.
    if (EXCEL_INTENT.test(userText) && !FUNDER_DOC.test(userText) && currentGrantData) {
      const grantTitle = currentGrantData.opportunityIdentification?.callTitle || currentGrantData.title || "this grant";
      setMessages((prev) => [
        ...prev,
        { role: "user", text: userText },
        { role: "ai", type: "answer", text: `Generating your Excel export for **${grantTitle}** — check your downloads. It contains every extracted field.` },
      ]);
      setInput("");
      try { exportToExcel(currentGrantData); } catch {}
      return;
    }

    // Must run BEFORE the user message is appended: startNewSession clears
    // messages, so pushing first would wipe the very message we are handling.
    if (openInNewTab) startNewSession();

    setMessages((prev) => [...prev, { role: "user", text: userText }]);
    setInput("");
    setLoading(true);
    try {
      if (chatMode === 'propose') {
        // Proposal writing mode — web-search-grounded proposal generation
        setMessages((prev) => [...prev, { role: "ai", type: "progress", message: "🔍 Researching funder landscape and current data…", percent: 20 }]);
        const res = await apiFetch(`${API}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId,
            type: 'propose',
            question: userText,
            orgProfile: orgProfile || {},
            grantContext: currentGrantData || null,
            writingStyle,
            history: messages.filter(m => m.role && m.text).slice(-6).map(m => ({ role: m.role, text: m.text })),
          }),
        });
        const responseText = await res.text();
        let json;
        try { json = JSON.parse(responseText); }
        catch { throw new Error(`Server error (${res.status}): ${responseText.slice(0, 200)}`); }
        if (json.success) {
          setMessages((prev) => [
            ...prev.filter(m => m.type !== "progress"),
            { role: "ai", type: "answer", text: json.answer, sources: json.sources || [] },
          ]);
        } else {
          setMessages((prev) => [...prev.filter(m => m.type !== "progress"), { role: "ai", type: "error", text: json.error }]);
        }
      } else if (isFirstNOFO || openInNewTab) {
        await extractWithStream(userText);
      } else {
        const reqType = hasNOFO ? "question" : "general";
        const streamId = `stream_${Date.now()}`;
        setMessages((prev) => [...prev, { id: streamId, role: "ai", type: "streaming", text: "" }]);

        const ctrl = new AbortController();
        streamAbortRef.current = ctrl;
        setIsStreaming(true);

        try {

        const streamRes = await apiFetch(`${API}/chat-stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type: reqType, question: userText, history: messages.filter(m => m.role && m.text).map((m) => ({ role: m.role, text: m.text })), grantContext: reqType === 'question' ? currentGrantData : undefined, orgProfile: orgProfile || undefined }),
          signal: ctrl.signal,
        });

        if (!streamRes.ok) {
          const errData = await streamRes.json().catch(() => ({}));
          if (errData.error?.includes('No NOFO loaded')) {
            setHasNOFO(false);
            setCurrentGrantData(null);
            setMessages((prev) => prev.map((m) => m.id === streamId ? { ...m, type: "answer", text: "Your session expired — please paste the grant URL or text again to continue." } : m));
          } else {
            setMessages((prev) => prev.map((m) => m.id === streamId ? { ...m, type: "error", text: errData.error || `Error ${streamRes.status}` } : m));
          }
        } else {
          const reader  = streamRes.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let curEvent = "";
          let fullText = "";
          let webSources = [];

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop();
            for (const line of lines) {
              if (line.startsWith("event: ")) { curEvent = line.slice(7).trim(); }
              else if (line.startsWith("data: ")) {
                try {
                  const parsed = JSON.parse(line.slice(6).trim());
                  if (curEvent === "token") {
                    fullText += parsed.text || "";
                    const tokenText = fullText;
                    setMessages((prev) => prev.map((m) => m.id === streamId ? { ...m, text: tokenText } : m));
                  } else if (curEvent === "sources") {
                    webSources = parsed.sources || [];
                  } else if (curEvent === "done") {
                    const doneText = parsed.answer || fullText;
                    const doneSources = webSources;
                    setMessages((prev) => prev.map((m) => m.id === streamId ? { ...m, type: "answer", text: doneText, sources: doneSources } : m));
                  } else if (curEvent === "error") {
                    setMessages((prev) => prev.map((m) => m.id === streamId ? { ...m, type: "error", text: parsed.message } : m));
                  }
                } catch {}
              }
            }
          }
        }

        } catch (err) {
          if (err.name === "AbortError") {
            // User pressed stop — finalize whatever text has arrived so far.
            setMessages((prev) => prev.map((m) =>
              m.id === streamId && m.type === "streaming"
                ? { ...m, type: "answer", text: m.text || "(stopped)" }
                : m
            ));
          } else { throw err; }
        } finally {
          streamAbortRef.current = null;
          setIsStreaming(false);
        }
      }
    } catch (err) {
      setMessages((prev) => [...prev.filter(m => m.type !== "progress" && m.type !== "streaming"), { role: "ai", type: "error", text: err.message }]);
    }
    setLoading(false);
  };

  const stopStreaming = () => { streamAbortRef.current?.abort(); };

  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const [navCounts, setNavCounts] = useState({ active: 0, apps: 0 });

  const handleLogout = () =>
    apiFetch(`${API}/auth/logout`, { method: "POST" })
      .then(() => { setToken(null); setAuthUser(false); localStorage.removeItem('gos_ws_owner'); });

  const markChecklistItem = useCallback((id) => {
    setChecklistItems(prev => {
      if (prev[id]) return prev;
      const next = { ...prev, [id]: true };
      apiFetch(`${API}/onboarding`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ checklist_items: next }),
      }).catch(() => {});
      return next;
    });
  }, []);


  // ── TIPS LOGIC ──────────────────────────────────────────────
  const tryShowTip = useCallback((id, text, action) => {
    if (tipsMuted) return;
    if (sessionStorage.getItem("nofodesk_tip_shown")) return; // 1 per session
    if (tipsSeen.includes(id)) return;
    const weekKey = "nofodesk_tips_week";
    const tsKey   = "nofodesk_tips_week_ts";
    const weekTs  = parseInt(localStorage.getItem(tsKey) || "0");
    const weekCt  = parseInt(localStorage.getItem(weekKey) || "0");
    if (Date.now() - weekTs > 7 * 24 * 60 * 60 * 1000) {
      localStorage.setItem(weekKey, "0");
      localStorage.setItem(tsKey, String(Date.now()));
    } else if (weekCt >= 3) return;
    setActiveTip({ id, text, action });
  }, [tipsMuted, tipsSeen]);

  const dismissTip = useCallback(() => {
    if (!activeTip) return;
    const newSeen = [...tipsSeen, activeTip.id];
    setTipsSeen(newSeen);
    setActiveTip(null);
    sessionStorage.setItem("nofodesk_tip_shown", "1");
    const weekKey = "nofodesk_tips_week";
    const tsKey   = "nofodesk_tips_week_ts";
    localStorage.setItem(weekKey, String((parseInt(localStorage.getItem(weekKey) || "0")) + 1));
    if (!localStorage.getItem(tsKey)) localStorage.setItem(tsKey, String(Date.now()));
    apiFetch(`${API}/onboarding`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tips_seen: newSeen }),
    }).catch(() => {});
  }, [activeTip, tipsSeen]);

  // Tip rule 1: idle on extract view with no messages
  useEffect(() => {
    if (activeView !== "extract" || messages.length > 0 || activeTip || tipsMuted) return;
    const t = setTimeout(() => {
      tryShowTip("tip_extract_start", "Paste any grant URL or upload a PDF to extract funding details in seconds.");
    }, 5000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, messages.length]);

  // Tip rule 2: tracker is empty
  useEffect(() => {
    if (activeView !== "tracker" || activeTip) return;
    const activeGrantsState = window.__nofo_active_grants;
    if (activeGrantsState !== undefined && activeGrantsState === 0) {
      tryShowTip("tip_empty_tracker", "No grants in your tracker yet. Discover live opportunities matched to your focus areas.", { label: "Discover →", view: "discover" });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  // Tip rule 3: has extraction, not tried propose mode
  useEffect(() => {
    if (!hasNOFO || chatMode === "propose" || activeTip) return;
    const t = setTimeout(() => {
      tryShowTip("tip_try_propose", "You can draft a full proposal for this grant — switch to 'Develop Proposal' mode above.");
    }, 8000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNOFO]);

  // Tip rule 4: after 3rd extraction, suggest Drive sync
  const { extractions } = appSub;
  useEffect(() => {
    if (!extractions || extractions.used < 3 || activeTip) return;
    tryShowTip("tip_drive_sync", "Connect Google Drive to automatically save grant folders and documents.", { label: "Go to Settings", view: "settings" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractions?.used]);

  // Tip rule 5: deadline visibility after 3+ active grants
  useEffect(() => {
    if (activeView !== "tracker" || activeTip) return;
    const t = setTimeout(() => {
      tryShowTip("tip_calendar", "Track all your grant deadlines visually — check the Calendar view.", { label: "Open Calendar", view: "calendar" });
    }, 4000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView === "tracker"]);

  // Tip rule 6: settings visit, suggest org profile completion
  useEffect(() => {
    if (activeView !== "settings" || activeTip || orgProfile?.name) return;
    const t = setTimeout(() => {
      tryShowTip("tip_org_profile", "Complete your org profile so the AI can tailor grant recommendations and proposal drafts.");
    }, 3000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  // Tip rule 7: tracker with grants — nudge toward attaching documents
  useEffect(() => {
    if (activeView !== "tracker" || activeTip) return;
    const t = setTimeout(() => {
      tryShowTip("tip_attach_docs", "You can attach documents to any grant — open a grant row and use the Files section to upload or link from Google Drive or OneDrive.");
    }, 12000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  // Tip rule 8: settings visit with a complete profile — mention account management options
  useEffect(() => {
    if (activeView !== "settings" || activeTip || !orgProfile?.name) return;
    const t = setTimeout(() => {
      tryShowTip("tip_account_control", "You can change your password or permanently delete your account anytime from the bottom of this Settings page.");
    }, 10000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  // ── Chat session auto-save (debounced 2s after last message) ──
  useEffect(() => {
    if (!chatKey || !hasNOFO || messages.length === 0) return;
    const saveable = messages.filter(m => m.role && (m.type === "answer" || m.type === "extraction" || m.role === "user"));
    if (saveable.length === 0) return;
    if (chatSaveTimer.current) clearTimeout(chatSaveTimer.current);
    chatSaveTimer.current = setTimeout(() => {
      apiFetch(`${API}/chat-session`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          key: chatKey,
          messages: saveable,
          grantContext: currentGrantData || null,
          grantSource: currentGrantData?._source || chatKey,
        }),
      }).catch(() => {});
    }, 2000);
    return () => { if (chatSaveTimer.current) clearTimeout(chatSaveTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, chatKey, hasNOFO]);

  // ── Restore latest chat session after login ──────────────────
  useEffect(() => {
    if (!authUser || hasNOFO) return;
    // Sessions live server-side in chat_sessions, keyed to the user — but the
    // POINTER to the current one was only ever in localStorage. On a second
    // laptop there is no local key, so this used to return early and the user
    // saw an empty app despite their history being safely on the server.
    // /chat-session/latest exists for exactly this case; fall back to it.
    const savedKey = localStorage.getItem("nofodesk_chat_key");
    const url = savedKey
      ? `${API}/chat-session?key=${encodeURIComponent(savedKey)}`
      : `${API}/chat-session/latest`;
    apiFetch(url)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.grantContext || !data?.messages?.length) return;
        // /latest reports which session it returned; adopt that key locally so
        // this device stays in sync with the rest from here on.
        const key = savedKey || data.chatKey;
        const restoreMsg = {
          id: generateId(), role: "ai", type: "session_restored",
          grantSource: data.grantSource || key,
          grantTitle: data.grantContext?.opportunityIdentification?.callTitle || data.grantContext?.title || "",
          updatedAt: data.updatedAt,
        };
        setMessages([restoreMsg, ...data.messages]);
        setCurrentGrantData(data.grantContext);
        setHasNOFO(true);
        if (key) {
          setChatKey(key);
          try { localStorage.setItem("nofodesk_chat_key", key); } catch {}
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  // Auth loading
  if (authUser === null) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: TOKENS.bgApp }}>
        <div style={{ color: TOKENS.textMuted, fontSize: 16, fontFamily: FONT_UI }}>Loading…</div>
      </div>
    );
  }

  // Unauthenticated — show landing page
  if (authUser === false) {
    return (
      <LandingPage
        onAuthSuccess={(user) => {
          setToken(true);
          setAuthUser(user);
          apiFetch(`${API}/onboarding`, { cache: "no-store" })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (!data) return;
              setChecklistItems(data.checklist_items || {});
              setTipsSeen(data.tips_seen || []);
              // Existing-user detection (server-side): a user with grants or a
              // prior extraction on record skips the new-user onboarding beat
              // even on a fresh browser / cleared localStorage.
              if (data.role === "existing" || data.checklist_items?.extract) {
                const uid = user?.email || user?.id || "anon";
                const cur = loadOnboarding(uid) || {};
                if (!cur.firstRealExtraction) {
                  const next = { sampleViewed: false, profileDone: !!loadOrgProfile(), deadlineSaved: false, dismissed: false, ...cur, firstRealExtraction: true };
                  localStorage.setItem(onbKey(uid), JSON.stringify(next));
                  setOnboarding(next);
                  setActiveView("discover");
                }
              }
            })
            .catch(() => {});
        }}
      />
    );
  }

  return (
    <SubscriptionProvider authUser={authUser}>
    <ThemeCtx.Provider value={T}>
      <div style={{ height: "100vh", display: "flex", background: T.bgApp, color: T.text, fontFamily: FONT_UI, overflow: "hidden", transition: "background 0.25s ease, color 0.25s ease" }}>

        {pendingExtraction && (
          <ExtractionReviewModal
            data={pendingExtraction.data}
            sourceName={pendingExtraction.sourceName}
            saving={reviewSaving}
            onConfirm={handleConfirmExtraction}
            onSaveToApps={handleSaveToApps}
            onDiscard={handleDiscardExtraction}
            onReExtract={handleReExtract}
          />
        )}

        <Sidebar
          activeView={activeView}
          setActiveView={setActiveView}
          gosSubTab={gosSubTab}
          setGosSubTab={setGosSubTab}
          authUser={authUser}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          themeObj={T}
          themeMode={themeMode}
          setThemeMode={setThemeMode}
          navCounts={navCounts}
          onLogout={handleLogout}
          sessions={sessions}
          currentSessionId={currentSessionId}
          onNewChat={startNewSession}
          onLoadSession={handleLoadSession}
          onDeleteSession={handleDeleteSession}
          onboarding={onboarding}
          onOnboardingItem={(id) => {
            if (id === "sample") { runSampleReplay(); return; }
            if (id === "profile" && !orgProfile) { setActiveView("settings"); return; }
            setActiveView("extract");
            setTimeout(() => window.dispatchEvent(new CustomEvent("nofodesk:focus-input")), 250);
          }}
          onOnboardingDismiss={() => markOnboarding("dismissed", "onboarding_checklist_dismissed")}
        />


        {/* ── Main content ── */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: T.bgApp }}>

          {/* ── Contextual tip bar ── */}
          <TipBar
            tip={activeTip}
            T={T}
            onDismiss={dismissTip}
            onAction={() => {
              if (activeTip?.action?.view) setActiveView(activeTip.action.view);
              dismissTip();
            }}
          />

          {/* ── Top bar ── */}
          <div style={{ height: 46, display: "flex", alignItems: "center", padding: "0 16px", borderBottom: `1px solid ${T.borderDim}`, background: T.bgHeader, flexShrink: 0, justifyContent: "space-between", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {!isDesktop && (
                <button onClick={() => setSidebarOpen(o => !o)} aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"} style={{ padding: 6, background: "transparent", border: "none", color: T.textMuted, cursor: "pointer", borderRadius: 6, display: "flex", alignItems: "center" }}>
                  <Menu size={18} />
                </button>
              )}
              <span style={{ fontSize: 13, fontWeight: 500, color: T.textMuted, fontFamily: FONT_UI }}>
                {{ extract: "Grant Extractor", tracker: "NofoDesk", calendar: "Calendar", dashboard: "Dashboard", settings: "Settings", myGrants: "My Grants", pricing: "Plans & Billing" }[activeView] || ""}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {authUser && <WorkspaceSwitcher />}
              {authUser && <NotificationBell />}
            </div>
          </div>

          {backendStatus === "down" && activeView === "extract" && (
            <div style={{ background: T.mode === "light" ? T.conflictBg : "#1a0808", borderBottom: `1px solid ${T.mode === "light" ? "#F0B5B5" : "#7f1d1d"}`, padding: "10px 20px", fontSize: 13, color: T.mode === "light" ? "#991B1B" : "#fca5a5", lineHeight: 1.9, flexShrink: 0 }}>
              The server is temporarily unavailable. Please try again later.
            </div>
          )}

          {(isOffline || offlineSyncStatus) && (
            <div style={{ background: offlineSyncStatus === 'done' ? '#059669' : offlineSyncStatus === 'syncing' ? '#2563EB' : '#D97706', color: '#fff', textAlign: 'center', padding: '7px 16px', fontSize: 13, fontFamily: FONT_UI, flexShrink: 0, transition: 'background 0.3s' }}>
              {isOffline
                ? `Working offline — ${hasQueued() ? `${queueCount()} table(s) queued, will sync on reconnect` : 'changes will sync when connected'}`
                : offlineSyncStatus === 'syncing' ? 'Syncing offline changes...'
                : 'All changes synced ✓'}
            </div>
          )}

          {/* Content views */}
          {activeView === "tracker" ? (
            <div style={{ flex: 1, overflow: "hidden", display: "flex" }}><DeadlineDashboard defaultSubTab={gosSubTab} defaultPortFilter={gosPortFilter} defaultAppFilter={gosAppFilter} authUser={authUser} onNavCountsChange={setNavCounts} /></div>
          ) : activeView === "calendar" ? (
            <div style={{ flex: 1, overflowY: "auto" }}><CalendarView /></div>
          ) : activeView === "dashboard" ? (
            <div style={{ flex: 1, overflowY: "auto" }}><DashboardView setActiveView={setActiveView} setGosSubTab={setGosSubTab} setGosPortFilter={setGosPortFilter} setGosAppFilter={setGosAppFilter} /></div>
          ) : activeView === "settings" ? (
            <div style={{ flex: 1, overflowY: "auto" }}>
              <OrgProfileView onProfileChange={(p) => {
                setOrgProfile(p);
                if (p) saveOrgProfile(p);
                apiFetch(`${API}/db/org-profile`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ profile: p || {} }) }).catch(() => {});
                if (p) {
                  markChecklistItem("profile");
                  markOnboarding("profileDone", "onboarding_profile_completed");
                  setChecklistItems(prev => {
                    if (!prev["discover"]) setActiveView("discover");
                    return prev;
                  });
                }
              }} />
              <ChangePasswordSection authUser={authUser} />
              <PlanGate feature="driveSync"><DriveSettings /></PlanGate>
              {/* Tips mute toggle */}
              <div style={{ padding: "0 24px 16px" }}>
                <div style={{ background: T.bgCard || T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 10, padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontFamily: FONT_UI, fontSize: 14, fontWeight: 600, color: T.text }}>Contextual tips</div>
                    <div style={{ fontFamily: FONT_UI, fontSize: 12, color: T.textMuted, marginTop: 2 }}>Helpful nudges shown occasionally as you use NofoDesk</div>
                  </div>
                  <button
                    onClick={() => {
                      const next = !tipsMuted;
                      setTipsMuted(next);
                      localStorage.setItem("nofodesk_tips_muted", next ? "1" : "0");
                      if (next) setActiveTip(null);
                    }}
                    style={{
                      width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
                      background: tipsMuted ? T.border : "#5B4FE9",
                      position: "relative", flexShrink: 0, transition: "background 0.2s",
                    }}
                  >
                    <div style={{
                      position: "absolute", top: 3, left: tipsMuted ? 3 : 23, width: 18, height: 18,
                      borderRadius: "50%", background: "#fff", transition: "left 0.2s",
                    }} />
                  </button>
                </div>
              </div>
              <DataPrivacySection authUser={authUser} />
              <DeleteAccountSection onDeleted={() => {
                apiFetch(`${API}/auth/logout`, { method: "POST" }).catch(() => {});
                setToken(null);
                setAuthUser(false);
                localStorage.removeItem('gos_ws_owner');
              }} />
            </div>
          ) : activeView === "pricing" ? (
            <div style={{ flex: 1, overflowY: "auto" }}><PricingView authUser={authUser} /></div>
          ) : activeView === "budgets" ? (
            <BudgetPage currentGrantData={currentGrantData} orgProfile={orgProfile} />
          ) : activeView === "awards" ? (
            <AwardSetupPage />
          ) : activeView === "findings" ? (
            <FindingsPage />
          ) : activeView === "property" ? (
            <PropertyPage />
          ) : activeView === "effort" ? (
            <EffortPage />
          ) : activeView === "subrecipients" ? (
            <SubrecipientsPage />
          ) : activeView === "discover" ? (
            <div style={{ flex: 1, overflowY: "auto" }}>
              {!appSub.canPropose && (
                <div style={{ margin: "16px 24px 0", padding: "10px 16px", background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12, color: T.textMuted, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <span>📖 Browse only — <strong style={{ color: T.text }}>Available on Professional — $49/month</strong> for full discovery with eligibility matching</span>
                  <button onClick={() => window.dispatchEvent(new CustomEvent("nofodesk:open-pricing"))} style={{ padding: "6px 12px", background: T.gold, color: "#fff", border: "none", borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", fontFamily: FONT_UI }}>Upgrade</button>
                </div>
              )}
              <DiscoverView setActiveView={setActiveView} setInput={setInput} onExtractGrant={handleExtractGrant} />
            </div>
          ) : (
            <ChatPage
              sessions={sessions} currentSessionId={currentSessionId}
              onLoadSession={handleLoadSession} onDeleteSession={handleDeleteSession} onNewChat={startNewSession}
              messages={messages} orgProfile={orgProfile} loading={loading} hasNOFO={hasNOFO}
              isStreaming={isStreaming} input={input} setInput={setInput}
              chatMode={chatMode} setChatMode={setChatMode}
              writingStyle={writingStyle} setWritingStyle={setWritingStyle}
              currentGrantData={currentGrantData}
              alertDismissed={alertDismissed} setAlertDismissed={setAlertDismissed}
              bottomRef={bottomRef} inputRef={inputRef} fileRef={fileRef} attachFile={attachFile}
              sendMessage={sendMessage} stopStreaming={stopStreaming} handleKeyDown={handleKeyDown}
              setActiveView={setActiveView}
              handleMiniProfileSave={handleMiniProfileSave} handleDeadlineSave={handleDeadlineSave}
              markChecklistItem={markChecklistItem}
            />
        )}
        </div>{/* end main content */}

        <style>{`
          /* Follows the active theme — hardcoding dark made native controls
             (date pickers, scrollbars, autofill) render dark on light mode. */
          input, textarea { color-scheme: ${T.mode === "light" ? "light" : "dark"}; transition: background 0.25s ease, color 0.25s ease, border-color 0.25s ease; }
          input::placeholder, textarea::placeholder { color: ${T.textFaint}; }
          /* Global quality floor (design brief §8): visible focus rings everywhere */
          :focus-visible { outline: 2px solid ${TOKENS.gold}; outline-offset: 2px; border-radius: 4px; }
          /* Chat input focus ring — CSS-only so focus/blur never re-renders the tree */
          .nd-chat-input:focus-within { border-color: ${TOKENS.gold} !important; box-shadow: ${TOKENS.shadowGoldGlow}; }
        `}</style>

        {/* Product tour — last child so it layers over every view */}
        {showTour && (
          <ProductTour
            onNavigate={(view) => setActiveView(view)}
            onClose={(how) => { setShowTour(false); logOnboardingEvent(how === "done" ? "tour_completed" : "tour_skipped"); }}
          />
        )}
      </div>
    </ThemeCtx.Provider>
    </SubscriptionProvider>
  );
}
